import Tooltip, { TooltipProps } from '@atlaskit/tooltip';
import React, { FunctionComponent, ReactNode, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

export const EllipsisOverflowContainer = styled.span`
  white-space: pre;
  text-overflow: ellipsis;
  overflow: hidden;
  display: block;
  max-width: 100%;
`;

export interface EllipsisOverflowProps {
  children?: ReactNode;
  className?: string;
  position?: TooltipProps['position'];
  mousePosition?: TooltipProps['mousePosition'];
  testId?: string;
}

/**
 * Renders a span with overflow: hidden and text-overflow: ellipsis. If the text overflows, a tooltip with the full text will be added.
 */
const EllipsisOverflow: FunctionComponent<EllipsisOverflowProps> = ({
  children,
  className,
  position,
  mousePosition,
  testId,
}) => {
  const [isOverflowed, setIsOverflowed] = useState(true);
  const spanRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const ob = new ResizeObserver(() =>
      setTimeout(() => {
        if (spanRef.current) {
          // See https://stackoverflow.com/a/10017343/242365
          const newIsOverflowed =
            spanRef.current.offsetWidth < spanRef.current.scrollWidth;

          if (newIsOverflowed !== isOverflowed) {
            setIsOverflowed(newIsOverflowed);
          }
        }
      }, 0)
    );
    if (spanRef.current) {
      ob.observe(spanRef.current);
    }
    return () => {
      ob.disconnect();
    };
  });

  return (
    <EllipsisOverflowContainer className={className} ref={spanRef}>
      <Tooltip
        content={isOverflowed ? children : ''}
        mousePosition={mousePosition}
        position={position}
        tag="span"
      >
        {/* Inner span is necessary because @atlaskit/tooltip requires a child element */}
        <span data-testid={testId}>{children}</span>
      </Tooltip>
    </EllipsisOverflowContainer>
  );
};

export default EllipsisOverflow;
